import { Spin } from 'antd'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { useAuthSession } from '@/lib/session'
import { hasRouteAccess, hasRouteVisibility } from '@/routes/helpers'
import type { ProtectedRouteDescriptor } from '@/routes/types'
import { useAdvicePrepPaywall } from '@/features/adviceprep/hooks'
import { advicePrepPaywalledAreaLabel } from '@/features/adviceprep/paywall/constants'
import AdvicePrepPaywallScreen from '@/features/adviceprep/paywall/AdvicePrepPaywallScreen'

export default function ProtectedRouteGuard({ descriptor }: { descriptor: ProtectedRouteDescriptor }) {
  const location = useLocation()
  const session = useAuthSession()
  const { isLoading, isReady, permissions } = useAuthorization()
  const paywall = useAdvicePrepPaywall()

  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  if (isLoading || !isReady) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Spin size="large" />
      </div>
    )
  }

  const hasAccess = hasRouteAccess(permissions, descriptor.access)
  const hasVisibility = hasRouteVisibility(session, descriptor.visibility)

  const paywalledArea = paywall ? advicePrepPaywalledAreaLabel(descriptor.key) : null
  if (paywall && paywalledArea) {
    return <AdvicePrepPaywallScreen reason={paywall.reason} areaLabel={paywalledArea} />
  }

  if (!hasAccess || !hasVisibility) {
    return <Navigate to="/unauthorized" replace state={{ from: location.pathname, reason: 'route-access' }} />
  }

  if (descriptor.redirectTo) {
    return <Navigate to={descriptor.redirectTo} replace />
  }

  return descriptor.element
}
